import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  useCreateMetadocMetadataMutation,
  useUpdateMetadocMetadataMutation,
} from "services/metadoc-api";

import type {
  EditableMedicationValue,
  MedicationField,
  MedicationMetadataValue,
} from "../../utils/codeMetadataUtils";

type MedicationEditorState = {
  medications: EditableMedicationValue[];
  isCollapsed: boolean;
  isSaving: boolean;
  lastError: string | null;
};

type MedicationEditorAction =
  | { type: "MEDICATIONS_LOADED"; payload: EditableMedicationValue[] }
  | { type: "TOGGLE_COLLAPSE" }
  | {
      type: "UPDATE_FIELD";
      metadataId: number;
      field: MedicationField;
      value: string;
    }
  | { type: "SAVE_START" }
  | { type: "SAVE_SUCCESS" }
  | { type: "SAVE_ERROR"; error: string }
  | { type: "ADD_MEDICATION_START" }
  | { type: "ADD_MEDICATION_SUCCESS" }
  | { type: "ADD_MEDICATION_ERROR"; error: string }
  | { type: "ADD_MEDICATION"; payload: EditableMedicationValue };

type UseMedicationEditorArgs = {
  metadocId: number;
  activeDocumentGroupId?: number;
  initialMedications: EditableMedicationValue[];
};

export type AddMedicationPreset = {
  name: string;
  dosage: string;
  frequency: string;
  date: string;
  providerName: string;
};

type UseMedicationEditorReturn = {
  state: MedicationEditorState;
  handleToggleCollapse: () => void;
  handleFieldChange: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => void;
  handleFieldSave: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => Promise<void>;
  handleAddMedication: (preset: AddMedicationPreset) => Promise<void>;
  loadMedications: (medications: EditableMedicationValue[]) => void;
};

const initialState: MedicationEditorState = {
  medications: [],
  isCollapsed: false,
  isSaving: false,
  lastError: null,
};

const medicationEditorReducer = (
  state: MedicationEditorState,
  action: MedicationEditorAction,
): MedicationEditorState => {
  switch (action.type) {
    case "MEDICATIONS_LOADED":
      return {
        ...state,
        medications: action.payload,
      };

    case "TOGGLE_COLLAPSE":
      return {
        ...state,
        isCollapsed: !state.isCollapsed,
      };

    case "UPDATE_FIELD":
      return {
        ...state,
        medications: state.medications.map((medication) =>
          medication.id === action.metadataId
            ? { ...medication, [action.field]: action.value }
            : medication,
        ),
      };

    case "SAVE_START":
      return {
        ...state,
        isSaving: true,
        lastError: null,
      };

    case "SAVE_SUCCESS":
      return {
        ...state,
        isSaving: false,
      };

    case "SAVE_ERROR":
      return {
        ...state,
        isSaving: false,
        lastError: action.error,
      };

    case "ADD_MEDICATION_START":
      return {
        ...state,
        isSaving: true,
        lastError: null,
      };

    case "ADD_MEDICATION_SUCCESS":
      return {
        ...state,
        isSaving: false,
      };

    case "ADD_MEDICATION_ERROR":
      return {
        ...state,
        isSaving: false,
        lastError: action.error,
      };

    case "ADD_MEDICATION":
      return {
        ...state,
        medications: [action.payload, ...state.medications],
      };

    default:
      return state;
  }
};

/**
 * trims user-entered medication values and converts empty strings to null.
 */
const toNullableTrimmedValue = (value: string): null | string => {
  const normalized = value.trim();
  return normalized ? normalized : null;
};

/**
 * builds the structured medication payload expected by metadata mutations.
 */
const buildMedicationMetadataValue = (
  medication: EditableMedicationValue,
): MedicationMetadataValue => ({
  name: toNullableTrimmedValue(medication.name),
  dosage: toNullableTrimmedValue(medication.dosage),
  frequency: toNullableTrimmedValue(medication.frequency),
});

const buildMedicationMetadataValueFromPreset = (
  preset: AddMedicationPreset,
): MedicationMetadataValue => ({
  name: toNullableTrimmedValue(preset.name),
  dosage: toNullableTrimmedValue(preset.dosage),
  frequency: toNullableTrimmedValue(preset.frequency),
});

/*
manages medication card edits and persists structured medication metadata
*/
export const useMedicationEditor = ({
  metadocId,
  activeDocumentGroupId,
  initialMedications,
}: UseMedicationEditorArgs): UseMedicationEditorReturn => {
  const [state, dispatch] = useReducer(medicationEditorReducer, {
    ...initialState,
    medications: initialMedications,
  });
  const medicationsRef = useRef(state.medications);
  const [updateMetadocMetadata] = useUpdateMetadocMetadataMutation();
  const [createMetadocMetadata] = useCreateMetadocMetadataMutation();

  useEffect(() => {
    medicationsRef.current = state.medications;
  }, [state.medications]);

  const saveMedication = useCallback(
    async (metadataId: number, nextMedication: EditableMedicationValue) => {
      dispatch({ type: "SAVE_START" });

      try {
        await updateMetadocMetadata({
          metadataId,
          data: {
            date: nextMedication.date || null,
            provider_name: nextMedication.providerName || null,
            value: buildMedicationMetadataValue(nextMedication),
          },
        }).unwrap();

        dispatch({ type: "SAVE_SUCCESS" });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Failed to update medication metadata:", error);
        dispatch({ type: "SAVE_ERROR", error: errorMessage });
        KaboodleToaster.error("Failed to update medication. Please try again.");
        throw error;
      }
    },
    [updateMetadocMetadata],
  );

  const loadMedications = useCallback(
    (medications: EditableMedicationValue[]) => {
      dispatch({ type: "MEDICATIONS_LOADED", payload: medications });
    },
    [],
  );

  const handleToggleCollapse = useCallback(() => {
    dispatch({ type: "TOGGLE_COLLAPSE" });
  }, []);

  const handleFieldChange = useCallback(
    (metadataId: number, field: MedicationField, value: string) => {
      dispatch({ type: "UPDATE_FIELD", metadataId, field, value });
    },
    [],
  );

  const handleFieldSave = useCallback(
    async (metadataId: number, field: MedicationField, value: string) => {
      const medication = medicationsRef.current.find(
        (candidate) => candidate.id === metadataId,
      );
      if (!medication) {
        return;
      }

      const nextMedication = {
        ...medication,
        [field]: value,
      };

      await saveMedication(metadataId, nextMedication);
    },
    [saveMedication],
  );

  const handleAddMedication = useCallback(
    async (preset: AddMedicationPreset) => {
      if (
        !preset.name.trim() ||
        !preset.dosage.trim() ||
        !preset.frequency.trim() ||
        typeof activeDocumentGroupId !== "number"
      ) {
        throw new Error("Invalid medication preset data");
      }

      dispatch({ type: "ADD_MEDICATION_START" });

      try {
        const createdMetadata = await createMetadocMetadata({
          data: {
            metadoc: metadocId,
            type: "medication",
            document_group: activeDocumentGroupId,
            date: preset.date.trim() || undefined,
            provider_name: preset.providerName.trim() || undefined,
            value: buildMedicationMetadataValueFromPreset(preset),
          },
        }).unwrap();

        dispatch({
          type: "ADD_MEDICATION",
          payload: {
            id: createdMetadata.id,
            documentGroupId: createdMetadata.document_group,
            date: createdMetadata.date ?? preset.date.trim(),
            providerName:
              createdMetadata.provider_name ?? preset.providerName.trim(),
            name: preset.name.trim(),
            dosage: preset.dosage.trim(),
            frequency: preset.frequency.trim(),
          },
        });
        dispatch({ type: "ADD_MEDICATION_SUCCESS" });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Failed to create medication metadata:", error);
        dispatch({ type: "ADD_MEDICATION_ERROR", error: errorMessage });
        KaboodleToaster.error("Failed to add medication. Please try again.");
        throw error;
      }
    },
    [activeDocumentGroupId, createMetadocMetadata, metadocId],
  );

  return {
    state,
    handleToggleCollapse,
    handleFieldChange,
    handleFieldSave,
    handleAddMedication,
    loadMedications,
  };
};
